import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  ListItemText,
  FormControl,
  InputLabel,
  IconButton,
  OutlinedInput,
  Tooltip,
} from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { fetchJobsHistory } from "@/services/jobs-api";
import type { HistoryJobDto, JobStatus, JobType } from "@/types/jobs";
import {
  JobDetailDialog,
  STATUS_LABEL,
  STATUS_COLOR,
  JOB_TYPE_LABEL,
  formatDateTime,
} from "./JobDetailDialog";
import { JOB_TYPE_DESIGN, MONO_SX, CHIP_GEOMETRIC_SX } from "./jobs-design-tokens";

const PAGE_SIZE = 10;

const ALL_STATUSES: JobStatus[] = ["Queued", "Running", "Completed", "Failed"];
const ALL_JOB_TYPES: JobType[] = ["BookingReconciliation", "ContractAssignment", "EligibleInvoicesReport", "CurrencyRateSync"];

/** Colores de fondo sutiles por estado */
const STATUS_BG: Record<string, string> = {
  Queued:    "rgba(237, 108, 2, 0.06)",
  Running:   "rgba(2, 136, 209, 0.06)",
  Completed: "rgba(46, 125, 50, 0.06)",
  Failed:    "rgba(211, 47, 47, 0.06)",
};

const STATUS_BORDER: Record<string, string> = {
  Queued:    "rgba(237, 108, 2, 0.25)",
  Running:   "rgba(2, 136, 209, 0.25)",
  Completed: "rgba(46, 125, 50, 0.25)",
  Failed:    "rgba(211, 47, 47, 0.25)",
};


function inferService(jobType: string): "core" | "finance" {
  return jobType === "EligibleInvoicesReport" ? "finance" : "core";
}

function toDisplayJob(job: HistoryJobDto) {
  let parsedResult: unknown;
  try {
    parsedResult = job.resultPayload ? JSON.parse(job.resultPayload) : undefined;
  } catch {
    parsedResult = undefined;
  }
  return { ...job, service: inferService(job.jobType), parsedResult };
}

// ─── Paginación ───────────────────────────────────────────────────────────────

function PaginationControls({
  page,
  total,
  onPrev,
  onNext,
}: {
  page: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        px: 2,
        py: 0.5,
        gap: 0.5,
        borderTop: "1px solid",
        borderColor: "divider",
      }}
    >
      <Typography component="span" sx={{ fontSize: "12px", color: "text.secondary", fontFamily: "monospace" }}>
        Pág. {page + 1} / {totalPages}
      </Typography>
      <Typography component="span" sx={{ fontSize: "11px", color: "text.disabled" }}>
        ({total})
      </Typography>
      <Tooltip title="Página anterior">
        <span>
          <IconButton size="small" onClick={onPrev} disabled={page === 0}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Página siguiente">
        <span>
          <IconButton size="small" onClick={onNext} disabled={page >= totalPages - 1}>
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}

// ─── Historia card ────────────────────────────────────────────────────────────

function HistoryJobCard({ job, onClick, index }: { job: HistoryJobDto; onClick: () => void; index: number }) {
  const typeDesign = JOB_TYPE_DESIGN[job.jobType] ?? { primary: "#94a3b8", bg: "rgba(148,163,184,0.06)", border: "rgba(148,163,184,0.25)", text: "#64748b" };
  return (
    <Box
      onClick={onClick}
      sx={{
        py: 1.25,
        px: 2,
        mx: 1.5,
        my: 0.5,
        borderRadius: "8px",
        border: "1px solid",
        borderColor: "divider",
        borderLeft: "4px solid",
        borderLeftColor: typeDesign.primary,
        bgcolor: index % 2 === 0 ? (STATUS_BG[job.status] ?? "transparent") : "transparent",
        cursor: "pointer",
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
        "&:hover": { bgcolor: "rgba(0,0,0,0.03)", boxShadow: "0 2px 6px rgba(0,0,0,0.08)" },
        transition: "background-color 0.15s, box-shadow 0.15s",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.25 }}>
        <Typography variant="body2" fontWeight={700} noWrap sx={{ flex: 1, mr: 1, fontSize: "13px", color: typeDesign.text }}>
          {JOB_TYPE_LABEL[job.jobType] ?? job.jobType}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
          <OpenInNewIcon sx={{ fontSize: 13, color: "text.disabled" }} />
          <Chip
            label={STATUS_LABEL[job.status] ?? job.status}
            color={STATUS_COLOR[job.status] ?? "default"}
            size="small"
            sx={{ fontSize: "0.68rem", height: 20, borderRadius: "6px" }}
          />
        </Box>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <Typography component="span" sx={{ ...MONO_SX, color: "text.disabled" }}>#{job.id}</Typography>
        {job.requestedBy && (
          <Typography variant="caption" color="text.secondary">· {job.requestedBy}</Typography>
        )}
        <Typography variant="caption" sx={{ fontSize: "11px", color: "text.disabled" }}>· {formatDateTime(job.createdOn)}</Typography>
      </Box>
    </Box>
  );
}

// ─── Tab principal ────────────────────────────────────────────────────────────

export function JobHistoryTab() {
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<JobStatus | "">("");
  const [typeFilter, setTypeFilter] = useState<JobType | "">("");
  const [jobs, setJobs] = useState<HistoryJobDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedJob, setSelectedJob] = useState<HistoryJobDto | null>(null);

  const load = useCallback(async (p: number, status: JobStatus | "", type: JobType | "") => {
    setLoading(true);
    setError(null);
    try {
      const activeStatus = status ? [status] : undefined;
      const activeType = type ? [type] : undefined;
      const result = await fetchJobsHistory(p, PAGE_SIZE, activeStatus, activeType);
      setJobs(result.rows);
      setTotal(result.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error al cargar histórico");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(page, statusFilter, typeFilter);
  }, [page, statusFilter, typeFilter, load]);

  const handlePrev = () => setPage((p) => Math.max(0, p - 1));
  const handleNext = () => setPage((p) => p + 1);

  const handleStatusChange = (value: JobStatus | "") => {
    setStatusFilter(value);
    setPage(0);
  };

  const handleTypeChange = (value: JobType | "") => {
    setTypeFilter(value);
    setPage(0);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Filtros */}
      <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider", display: "flex", gap: 1 }}>
        <FormControl size="small" sx={{ flex: 1, minWidth: 0, "& .MuiOutlinedInput-root": { borderRadius: "6px" } }}>
          <InputLabel id="history-status-filter-label" sx={{ fontSize: "0.8rem" }}>
            Estado
          </InputLabel>
          <Select
            labelId="history-status-filter-label"
            value={statusFilter}
            onChange={(e) => handleStatusChange(e.target.value as JobStatus | "")}
            input={<OutlinedInput label="Estado" />}
            sx={{ fontSize: "0.82rem", "& .MuiSelect-select": { py: "8.5px" } }}
          >
            <MenuItem value="" dense>
              <ListItemText primary="Todos" primaryTypographyProps={{ fontSize: "0.82rem" }} />
            </MenuItem>
            {ALL_STATUSES.map((s) => (
              <MenuItem key={s} value={s} dense>
                <ListItemText primary={STATUS_LABEL[s]} primaryTypographyProps={{ fontSize: "0.82rem" }} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ flex: 1, minWidth: 0, "& .MuiOutlinedInput-root": { borderRadius: "6px" } }}>
          <InputLabel id="history-type-filter-label" sx={{ fontSize: "0.8rem" }}>
            Tipo
          </InputLabel>
          <Select
            labelId="history-type-filter-label"
            value={typeFilter}
            onChange={(e) => handleTypeChange(e.target.value as JobType | "")}
            input={<OutlinedInput label="Tipo" />}
            sx={{ fontSize: "0.82rem", "& .MuiSelect-select": { py: "8.5px" } }}
          >
            <MenuItem value="" dense>
              <ListItemText primary="Todos" primaryTypographyProps={{ fontSize: "0.82rem" }} />
            </MenuItem>
            {ALL_JOB_TYPES.map((t) => (
              <MenuItem key={t} value={t} dense>
                <ListItemText primary={JOB_TYPE_LABEL[t] ?? t} primaryTypographyProps={{ fontSize: "0.82rem" }} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Paginación superior */}
      <PaginationControls page={page} total={total} onPrev={handlePrev} onNext={handleNext} />

      {/* Lista con scroll */}
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        ) : jobs.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No hay jobs con los filtros seleccionados
            </Typography>
          </Box>
        ) : (
          jobs.map((job, idx) => (
            <HistoryJobCard key={job.id} job={job} index={idx} onClick={() => setSelectedJob(job)} />
          ))
        )}
      </Box>

      {/* Paginación inferior */}
      <PaginationControls page={page} total={total} onPrev={handlePrev} onNext={handleNext} />

      {/* Dialog de detalle */}
      {selectedJob && (
        <JobDetailDialog
          job={toDisplayJob(selectedJob)}
          onClose={() => setSelectedJob(null)}
        />
      )}
    </Box>
  );
}
